import React from "react"
import * as graphql from "lib/types/generated/graphql-types"
import { StoreGroupSearchHit } from "lib/types/generated/graphql-types"
import Money from "../../../../Money"
import DineroFactory from "dinero.js"
import { getCountryName } from "helpers/countries"
import { getPaymentMethodDisplayName } from "helpers/getPaymentDisplayName"
import { Compensation as CompensationClass } from "../../../../../classes/order/Compensation"
import { Refund as RefundClass } from "../../../../../classes/order/Refund"
import { Release as ReleaseClass } from "../../../../../classes/order/Release"
import { Cancellation } from "../../../../../classes/order/Cancellation"
import {
  OrderDate,
  OrderInfo,
  OrderTotalsContainer,
  OrderValueWrapper,
  RowWrapper,
  TotalOrderValue,
  TotalOrderValueTextLeft,
  TotalOrderValueTextRight,
  TotalOrderValueWrapper,
  TotalsInfo,
  TotalValueLeft,
  TotalValueRight,
  TotalWrapper
} from "../OrderTotals.styled"
import dayjs from "dayjs"

type Props = {
  order: graphql.Order
  storeGroups: StoreGroupSearchHit[]
}

const OrderTotals = ({ order, storeGroups }: Props) => {
  const getStoreGroupName = (id: string) => {
    const storeGroup = storeGroups?.find((storeGroup: StoreGroupSearchHit) => storeGroup.id === id)
    return storeGroup?.name ?? id
  }

  const giftCardTotal = order.totals.giftCardTotal
  const bonusTotal = order.totals.bonusTotal

  const compensations = new CompensationClass(order.Compensations)
  const refunds = new RefundClass(order.Refunds)
  const releases = new ReleaseClass(order.Releases)
  const cancellation = new Cancellation(order.Cancellations)

  const totalCompensationFee = compensations.compensationFeeTotal()

  const compensatedPayment = compensations
    .completedCompensations()
    .reduce((acc, compensation) => acc + (compensation.payment?.amount ?? 0), 0)

  const refundedAmount =
    refunds.completedRefunds().reduce((acc, refund) => acc + refund.totals.grandTotal, 0) ?? 0

  const refundFeeAmount =
    refunds.completedRefunds().reduce((acc, refund) => acc + (refund?.fee?.amount ?? 0), 0) ?? 0

  const releasedAmount =
    releases
      .completedReleases()
      .map((release) => release.orderLines.reduce((a, b) => a + b.totalAmount, 0) ?? 0)
      .reduce((acc, amount) => acc + amount, 0) ?? 0

  const returnedAmount = refundedAmount + compensatedPayment + releasedAmount
  const showCustomerHasPaid = order.isTaxIncludedInPrice || order.Compensations.length === 0

  const getCustomerPaid = () => {
    if (cancellation.orderCancelled(order.id)) {
      return 0
    }

    return order.totals.grandTotal - returnedAmount
  }

  return (
    <OrderTotalsContainer>
      <OrderValueWrapper>
        <div>
          <TotalOrderValueTextLeft>Order value</TotalOrderValueTextLeft>
          <TotalValueLeft>
            <Money
              amount={order.totals.subTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalValueLeft>
        </div>
        <OrderDate>{dayjs(order.date).format("YYYY-MM-DD HH:mm")}</OrderDate>
      </OrderValueWrapper>
      <RowWrapper>
        <OrderInfo>
          <b>Store</b>
          <div>
            {getCountryName(order.countryCode)}/ {getStoreGroupName(order.storeGroupId)}
          </div>
        </OrderInfo>
        <OrderInfo>
          <b>Payment</b>
          <div>
            {order?.Payment?.providerName}, {getPaymentMethodDisplayName(order?.Payment?.method)}
          </div>
        </OrderInfo>
        {order?.Shipping?.providerName && (
          <OrderInfo>
            <b>Shipping</b>
            <div>
              {order?.Shipping?.providerName}: {order?.shippingFees?.map((fee) => fee.name)}
            </div>
          </OrderInfo>
        )}
      </RowWrapper>
      <TotalWrapper>
        Subtotal{" "}
        <Money
          amount={order.totals.subTotal}
          currencyUnit={order.currencyCode as DineroFactory.Currency}
        />
      </TotalWrapper>
      <RowWrapper>
        <TotalsInfo>
          <div>Shipping fee</div>
          <Money
            amount={order.totals.shippingTotal}
            currencyUnit={order.currencyCode as DineroFactory.Currency}
          />
        </TotalsInfo>
        {order.totals.discountTotal > 0 && (
          <TotalsInfo>
            <div>Total discount</div>-{" "}
            <Money
              amount={order.totals.discountTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsInfo>
        )}
        {bonusTotal > 0 && (
          <TotalsInfo>
            <div>Bonus amount</div>-{" "}
            <Money
              amount={bonusTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsInfo>
        )}
        {giftCardTotal > 0 && (
          <TotalsInfo>
            <div>Gift card</div>-{" "}
            <Money
              amount={giftCardTotal}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </TotalsInfo>
        )}
      </RowWrapper>
      <TotalWrapper>
        Grand total{" "}
        <Money
          amount={order.totals.grandTotal}
          currencyUnit={order.currencyCode as DineroFactory.Currency}
        />
      </TotalWrapper>
      {(refundedAmount > 0 ||
        refundFeeAmount > 0 ||
        compensatedPayment > 0 ||
        totalCompensationFee > 0 ||
        releasedAmount > 0 ||
        cancellation.orderCancelled(order.id)) && (
        <RowWrapper>
          {refundedAmount > 0 && (
            <TotalsInfo>
              <div>Refunded
                {refundFeeAmount > 0 &&
                  <> (Fee: <Money amount={refundFeeAmount} currencyUnit={order.currencyCode as DineroFactory.Currency} />)</>}
              </div>-{" "}
              <Money
                amount={refundedAmount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsInfo>
          )}
          {compensatedPayment > 0 && (
            <TotalsInfo>
              <div>
                Compensated{" "}
                {totalCompensationFee > 0 &&
                  <>(Fee: <Money amount={totalCompensationFee} currencyUnit={order.currencyCode as DineroFactory.Currency} />)</>}
              </div>
              <div>
                -{" "}
                <Money
                  amount={compensatedPayment}
                  currencyUnit={order.currencyCode as DineroFactory.Currency}
                />
                {!order.isTaxIncludedInPrice && " (Inc. VAT)"}
              </div>
            </TotalsInfo>
          )}
          {releasedAmount > 0 && (
            <TotalsInfo>
              <div>Released</div>-{" "}
              <Money
                amount={releasedAmount}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsInfo>
          )}
          {cancellation.orderCancelled(order.id) && (
            <TotalsInfo>
              <div>Cancelled</div>-{" "}
              <Money
                amount={order.totals.grandTotal}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalsInfo>
          )}
        </RowWrapper>
      )}
      {showCustomerHasPaid && (
        <TotalOrderValueWrapper>
          <TotalOrderValue>
            <TotalOrderValueTextRight>Customer has paid</TotalOrderValueTextRight>
            <TotalValueRight>
              <Money
                amount={getCustomerPaid()}
                currencyUnit={order.currencyCode as DineroFactory.Currency}
              />
            </TotalValueRight>
          </TotalOrderValue>
        </TotalOrderValueWrapper>
      )}
    </OrderTotalsContainer>
  )
}

export default OrderTotals
